import { lazy, Suspense } from 'react';
import type { DocKind } from '@shared/api';
import { useRouter } from '../router';
import { DocumentSkeleton } from './DocumentSkeleton';

/**
 * One document, whatever its kind. `/docs/:id`, `/sheets/:id`, `/slides/:id`.
 *
 * Docs, Sheets and Slides. Each is its own chunk, loaded when it is opened (or a moment before:
 * core/prefetch.ts starts it when Home goes idle, for Docs, and when a card is pointed at), and
 * not with the home page: ProseMirror, Yjs and the formula engine are most of the app's weight,
 * and a reader who only lists documents never needs them.
 *
 * While it loads, the editor's outline is on screen (DocumentSkeleton), in the main chunk, so the
 * frame after a press is already the editor.
 *
 * This page is rendered for a link guest too, so it asks the API who the caller
 * is (GET /documents/:id) and does not assume a signed-in person (state.tsx).
 */
const DocsPage = lazy(() => import('../docs/DocsPage'));
const SheetsPage = lazy(() => import('../sheets/SheetsPage'));
const SlidesPage = lazy(() => import('../slides/SlidesPage'));

export function Editor({ kind, id }: { kind: DocKind; id: string }) {
  const { query } = useRouter();
  if (kind === 'slides') {
    // An audience window is a slide on black from its first frame, not the editor's outline.
    const audience = query.get('present') === 'audience';
    return (
      <Suspense fallback={audience ? <div aria-busy="true" style={{ position: 'fixed', inset: 0, background: '#000' }} /> : <DocumentSkeleton kind="slides" />}>
        <SlidesPage id={id} />
      </Suspense>
    );
  }

  if (kind === 'sheets') {
    return (
      <Suspense fallback={<DocumentSkeleton kind="sheets" />}>
        <SheetsPage id={id} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<DocumentSkeleton kind="docs" />}>
      <DocsPage id={id} />
    </Suspense>
  );
}
